// frontend/src/components/auth/SessionExpiredProvider.tsx
//
// Registers the api.ts session-expired handler (setSessionExpiredHandler) so
// an expired session surfaces as a soft in-app modal instead of the legacy
// hard window.location redirect that discarded all in-progress work (OA-7).
//
// The current route (path + query) is captured at the moment the session
// dies and passed to the login page as `state.returnTo`, so a successful
// re-login lands the user back where they were.
//
// Mount ONCE inside the router (App.tsx wraps <Routes> with it) so the
// handler can use react-router navigation.

import type { ReactNode } from 'react'
import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Modal, Btn } from '../ui/index.jsx'
import { setSessionExpiredHandler } from '../../utils/api.js'
import { useLanguage } from '../../i18n/index.jsx'

interface SessionExpiredProviderProps {
  children: ReactNode
}

interface ModalState {
  open: boolean
  isPlatform: boolean
  reason: string | null
  returnTo: string
}

const CLOSED: ModalState = { open: false, isPlatform: false, reason: null, returnTo: '' }

export default function SessionExpiredProvider({ children }: SessionExpiredProviderProps) {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()

  const [modal, setModal] = useState<ModalState>(CLOSED)

  // The handler fires from outside React's render cycle — keep the latest
  // location in a ref so the captured returnTo is always current.
  const locationRef = useRef(location)
  locationRef.current = location

  useEffect(() => {
    setSessionExpiredHandler((isPlatform, reason) => {
      const loc = locationRef.current
      setModal({
        open: true,
        isPlatform,
        reason: reason || null,
        returnTo: `${loc.pathname}${loc.search}`,
      })
    })
    return () => setSessionExpiredHandler(null)
  }, [])

  const handleSignIn = useCallback(() => {
    const { isPlatform, reason, returnTo } = modal
    setModal(CLOSED)
    const loginPath = isPlatform ? '/platform-login' : '/login'
    navigate(reason ? `${loginPath}?reason=${reason}` : loginPath, {
      state: { returnTo },
    })
  }, [modal, navigate])

  return (
    <>
      {children}
      {modal.open && (
        <Modal
          open
          onClose={handleSignIn}
          title={t('errorSessionTitle')}
          size="sm"
        >
          <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)' }} data-testid="session-expired-body">
            {modal.reason === 'session_revoked' ? t('sessionRevoked') : t('errorSessionBody')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Btn variant="primary" onClick={handleSignIn} data-testid="session-expired-signin">
              {t('errorSessionAction')}
            </Btn>
          </div>
        </Modal>
      )}
    </>
  )
}
